import React, { useEffect, useState, useContext } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Stack,
  Divider,
  Avatar,
  Button,
  Chip,
} from "@mui/material";
import { CartContext } from "../context/CartContext";
import { API_BASE } from "../utils/env";

const currency = (cents, c = "cad") =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: c.toUpperCase(),
  }).format((cents || 0) / 100);

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const { clearCart } = useContext(CartContext);

  useEffect(() => {
    if (!sessionId) return;
    fetch(`${API_BASE}/api/orders/checkout-session/${sessionId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setData(d);
        clearCart();
      })
      .catch(() => setErr(true));
  }, [sessionId, API_BASE, clearCart]);

  if (!sessionId) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Thanks! 🎉
        </Typography>
        <Typography>
          No session id in the URL, but your payment likely completed.
        </Typography>
        <Button
          component={RouterLink}
          to="/"
          sx={{ mt: 3 }}
          variant="contained"
        >
          Back home
        </Button>
      </Container>
    );
  }

  if (err) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          We couldn’t verify your order
        </Typography>
        <Typography gutterBottom>
          Please check your email for a receipt, or try again.
        </Typography>
        <Button
          component={RouterLink}
          to="/cart"
          sx={{ mt: 3 }}
          variant="contained"
        >
          Back to cart
        </Button>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h5" fontWeight={700}>
          Confirming your order…
        </Typography>
      </Container>
    );
  }

  const totalText = currency(data.amount_total, data.currency);

  return (
    <Container sx={{ py: 6 }}>
      <Grid container spacing={4}>
        {/* Left: Thanks + Items */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography variant="h4" fontWeight={800}>
                Thank you!
              </Typography>
              <span role="img" aria-label="party">
                🎉
              </span>
            </Stack>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Order confirmed. A receipt was sent to{" "}
              <strong>{data.customer_email || "your email"}</strong>.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
              Items
            </Typography>

            <Stack spacing={2}>
              {data.line_items.map((li, i) => {
                const name = li.name;
                const size = li.size ? ` (${li.size})` : "";
                const qty = li.quantity || 1;
                const unit = currency(li.unit_amount, li.currency);
                const subtotal = currency(li.amount_subtotal, li.currency);
                return (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size={{ xs: 12, sm: 2 }}>
                        <Avatar
                          variant="rounded"
                          src={li.image || undefined}
                          alt={name}
                          sx={{
                            width: 72,
                            height: 72,
                            mx: { xs: "auto", sm: 0 },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 7 }}>
                        <Typography fontWeight={700} noWrap title={name}>
                          {name}
                          <Typography component="span" color="text.secondary">
                            {size}
                          </Typography>
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mt={0.5}
                        >
                          <Chip size="small" label={`Qty ${qty}`} />
                          <Typography variant="body2" color="text.secondary">
                            Unit: {unit}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography align="right" fontWeight={800}>
                          {subtotal}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>

            <Box mt={3}>
              <Button
                component={RouterLink}
                to="/"
                variant="contained"
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Continue shopping
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Summary */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Order Summary
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography color="text.secondary">Payment status</Typography>
              <Typography fontWeight={700}>
                {data.payment_status?.replace(/_/g, " ") || "paid"}
              </Typography>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={800}>
                Total
              </Typography>
              <Typography variant="subtitle1" fontWeight={800}>
                {totalText}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
